import React, {useMemo, useState} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Grid,
    Paper,
    Chip,
    Collapse,
    IconButton,
    Avatar,
    LinearProgress,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import {alpha, styled} from "@mui/material/styles";
import DescriptionIcon from "@mui/icons-material/Description";
import WorkOutlineIcon from "@mui/icons-material/WorkOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreateRoundedIcon from "@mui/icons-material/CreateRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {addDemande} from "../../../../services/DemandeService";
import {notifySuccess, notifyError} from "../../../../components/CustomToast";

const OPTIONS = [
    {value: "Attestation de Salaire", icon: PaidOutlinedIcon, hint: "Revenus / banque"},
    {value: "Attestation de Travail", icon: WorkOutlineIcon, hint: "Lien d’emploi"},
    {value: "Attestation de Domiciliation de Salaire", icon: AccountBalanceIcon, hint: "Domiciliation bancaire"},
    {value: "autre", icon: CreateRoundedIcon, hint: "Spécifiez le type"},
];

const TEMPLATES = [
    {label: "Banque", text: "Attestation demandée pour dossier bancaire."},
    {label: "Ambassade", text: "Attestation destinée au dossier consulaire / ambassade."},
    {label: "Visa", text: "Attestation requise pour demande de visa."},
];

const GlassPaper = styled(Paper)(({theme}) => ({
    borderRadius: 16,
    position: "relative",
    overflow: "hidden",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
    background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(
        theme.palette.background.paper,
        0.7
    )} 100%)`,
    boxShadow: "0 18px 60px rgba(0,0,0,.18)",
}));

const OptionTile = ({label, icon: Icon, selected, onClick, hint}) => (
    <GlassPaper
        role="button"
        onClick={onClick}
        elevation={0}
        sx={(theme) => ({
            p: 2,
            cursor: "pointer",
            height: "100%",
            transition: "all .15s ease",
            outline: selected ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
            "&:hover": {transform: "translateY(-2px)"},
        })}
    >
        <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar
                variant="rounded"
                sx={(theme) => ({
                    width: 42,
                    height: 42,
                    bgcolor: selected ? alpha(theme.palette.primary.main, 0.18) : alpha(theme.palette.grey[500], 0.12),
                    color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
                })}
            >
                <Icon/>
            </Avatar>
            <Box flex={1} minWidth={0}>
                <Typography fontWeight={700} noWrap title={label}>
                    {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {hint}
                </Typography>
            </Box>
            {selected && <CheckCircleRoundedIcon color="primary"/>}
        </Box>
    </GlassPaper>
);

const GradientHeader = ({onClose}) => (
    <Box
        sx={(theme) => ({
            px: 3,
            pt: 3,
            pb: 2,
            position: "relative",
            color: "#fff",
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        })}
    >
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                background:
                    "radial-gradient(900px 260px at 10% -20%, rgba(255,255,255,.22), transparent), radial-gradient(500px 180px at 110% 0%, rgba(255,255,255,.15), transparent)",
            }}
        />
        <Box display="flex" alignItems="center" justifyContent="space-between" position="relative">
            <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar variant="rounded" sx={{bgcolor: alpha("#fff", 0.22), color: "#fff", width: 46, height: 46}}>
                    <DescriptionIcon/>
                </Avatar>
                <Box>
                    <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                        Demander une attestation
                    </Typography>
                    <Typography variant="caption" sx={{opacity: 0.9}}>
                        Choisissez le type <ChevronRightRoundedIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> ajoutez une note <ChevronRightRoundedIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> envoyez
                    </Typography>
                </Box>
            </Box>

            <IconButton onClick={onClose} sx={{color: "#fff"}} aria-label="Fermer">
                <CloseRoundedIcon/>
            </IconButton>
        </Box>
    </Box>
);

const AccentBar = () => (
    <Box
        sx={(theme) => ({
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.secondary.main})`,
        })}
    />
);

const RequestAttestationModal = ({open, onClose, collaborateurId, onSuccess}) => {
    const [type, setType] = useState("");
    const [customType, setCustomType] = useState("");
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);

    const isAutre = type === "autre";
    const finalSousType = isAutre ? customType.trim() : type;

    const maxChars = 400;
    const charCount = description.length;
    const charPct = Math.min(100, Math.round((charCount / maxChars) * 100));

    const canSubmit = !!finalSousType && !loading;

    const handleSubmit = async () => {
        const payload = {
            type: "demande Attestation",
            sousType: finalSousType,
            collaborateur: [collaborateurId],
            dateDebut: null,
            dateFin: null,
            description: description?.trim() || null,
        };

        try {
            setLoading(true);
            await addDemande(payload);
            notifySuccess("Demande envoyée avec succès !");
            onSuccess?.();
            onClose();
        } catch {
            notifyError("Erreur lors de l'envoi de la demande");
        } finally {
            setLoading(false);
            setType("");
            setCustomType("");
            setDescription("");
        }
    };

    const livePreview = useMemo(
        () => (isAutre ? customType || "—" : type || "—"),
        [isAutre, customType, type]
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            slotProps={{ paper: {
                sx: (theme) => ({
                    borderRadius: 4,
                    overflow: "hidden",
                    boxShadow: "0 26px 70px rgba(0,0,0,0.28)",
                    background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${
                        theme.palette.background.paper
                    } 100%)`,
                }),
            } }}
        >
            <GradientHeader onClose={onClose}/>
            <AccentBar/>

            <DialogContent sx={{p: 3}}>
                <Grid container spacing={2.5}>
                    {OPTIONS.map(({value, icon, hint}) => (
                        <Grid item xs={12} sm={6} key={value}>
                            <OptionTile
                                label={value === "autre" ? "Autre…" : value}
                                icon={icon}
                                hint={hint}
                                selected={type === value}
                                onClick={() => setType(value)}
                            />
                        </Grid>
                    ))}
                </Grid>

                <Collapse in={isAutre} unmountOnExit>
                    <Box mt={2}>
                        <TextField
                            autoFocus
                            label="Préciser le type d’attestation"
                            placeholder="Ex. : Attestation de stage, Attestation de présence…"
                            fullWidth
                            value={customType}
                            onChange={(e) => setCustomType(e.target.value)}
                            variant="outlined"
                        />
                    </Box>
                </Collapse>

                <Box mt={3} display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <TipsAndUpdatesRoundedIcon fontSize="small" color="primary"/>
                    <Typography variant="body2" color="text.secondary">
                        Astuces rapides :
                    </Typography>
                    {TEMPLATES.map((t) => (
                        <Chip
                            key={t.label}
                            size="small"
                            variant="outlined"
                            label={t.label}
                            onClick={() => setDescription((prev) => (prev ? `${prev} ${t.text}` : t.text))}
                        />
                    ))}
                </Box>

                <Box mt={2}>
                    <TextField
                        label="Description (facultatif)"
                        placeholder="Ajoutez un commentaire pour préciser votre besoin…"
                        multiline
                        rows={4}
                        fullWidth
                        value={description}
                        onChange={(e) => {
                            if (e.target.value.length <= maxChars) setDescription(e.target.value);
                        }}
                        helperText={`${charCount}/${maxChars} caractères`} slotProps={{ formHelperText: {sx: {textAlign: "right"}} }}
                    />
                    <LinearProgress
                        variant="determinate"
                        value={charPct}
                        sx={(theme) => ({
                            mt: 1,
                            height: 6,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            "& .MuiLinearProgress-bar": {
                                borderRadius: 2,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            },
                        })}
                    />
                </Box>

                <Box
                    mt={3}
                    p={2}
                    borderRadius={2}
                    sx={(theme) => ({
                        border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                    })}
                >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">
                            Aperçu
                        </Typography>
                        <Tooltip title="Ce sera visible par l’équipe RH">
                            <AutoAwesomeRoundedIcon fontSize="small" color="primary"/>
                        </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={700}>
                            {livePreview}
                        </Typography>
                        {finalSousType && <Chip size="small" color="primary" label="Prêt à envoyer"/>}
                    </Box>
                    {!!description && (
                        <Typography variant="body2" color="text.secondary" mt={1}>
                            {description}
                        </Typography>
                    )}
                </Box>

            </DialogContent>

            <DialogActions sx={{px: 3, pb: 2, pt: 2}}>
                <Button
                    onClick={onClose}
                    sx={{borderRadius: 3, textTransform: "none", fontWeight: 500, color: "text.secondary"}}
                    disabled={loading}
                >
                    Annuler
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    startIcon={<RocketLaunchRoundedIcon/>}
                    sx={(theme) => ({
                        px: 3,
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: "bold",
                        minWidth: 170,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        boxShadow: "0px 8px 22px rgba(0,0,0,0.18)",
                        "&:hover": {
                            background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                        },
                    })}
                >
                    Envoyer la demande
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RequestAttestationModal;
